import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { functionalUpdate } from "@tanstack/react-table";
import { MdRotateLeft } from "react-icons/md";

import { Flex, HStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { useDSPAdCreatives } from "../hooks/useDSPProductAdCreatives";
import { toTitleCase } from "../../../utils/formatText";
import { Button } from "../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { createAppColumnHelper, useClientTable } from "../../../components/table/tableFeatures";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { commaSearchFilterFn } from "../../../components/table/filterFns";
import { resetTable, selectedIdsToSelectionState } from "../../../components/table/utils";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

const getFullLanguageName = (languageCode) => {
  try {
    return languageNames.of(languageCode);
  } catch {
    // An invalid language tag throws; show the raw code instead.
    return languageCode;
  }
};

const columnHelper = createAppColumnHelper();

const creativeSearchFilterFn = commaSearchFilterFn((row) => `${row.name}`);

export const CreativeSelector = ({ selectedCreatives = [], setSelectedCreatives = () => {} }) => {
  const { t } = useTranslation(["dsp", "common"]);
  const { data, isLoading } = useDSPAdCreatives();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: t(($) => $.dsp["Name"]),
        size: 300,
        enableHiding: false,
        sortFn: "alphanumeric",
      }),
      columnHelper.accessor((row) => row.adCreativeFormatProperties?.adCreativeFormatType ?? undefined, {
        id: "format",
        header: t(($) => $.dsp["Format"]),
        size: 150,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        cell: ({ getValue }) => toTitleCase(getValue()),
      }),
      columnHelper.accessor((row) => (row.language ? getFullLanguageName(row.language) : undefined), {
        id: "language",
        header: t(($) => $.dsp["Language"]),
        size: 160,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
      }),
      columnHelper.accessor(
        (row) =>
          row.adCreativeFormatProperties?.creativeSizes?.length === 0 ?
            t(($) => $.dsp["Responsive"])
          : t(($) => $.dsp["Multi-size"]),
        {
          id: "size",
          header: t(($) => $.dsp["Size"]),
          size: 130,
          enableResizing: false,
          sortFn: "alphanumeric",
        },
      ),
    ];
  }, [t]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "dspLineItemCreativeSelector",
    initialState: {
      pagination: { pageIndex: 0, pageSize: 5 },
      sorting: [{ id: "name", desc: false }],
    },
  });

  // The selection is the form's value; row ids are strings, so map back to the original id values.
  const rowSelection = selectedIdsToSelectionState(selectedCreatives);

  const table = useClientTable({
    data,
    columns,
    getRowId: (row) => String(row.adCreativeId),
    enableRowSelection: true,
    globalFilterFn: creativeSearchFilterFn,
    initialState,
    state: { ...state, rowSelection },
    ...setters,
    onRowSelectionChange: (updater) => {
      const next = functionalUpdate(updater, rowSelection);
      const kept = selectedCreatives.filter((id) => next[String(id)]);
      const keptKeys = new Set(kept.map(String));
      const added = (data ?? [])
        .map((creative) => creative.adCreativeId)
        .filter((id) => next[String(id)] && !keptKeys.has(String(id)));
      setSelectedCreatives([...kept, ...added]);
    },
    meta: {
      showSkeleton: isLoading,
    },
  });

  return (
    <Flex direction="column">
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => {
            // The selection is the form's value, not a table setting, so reset leaves it alone.
            const { rowSelection: selection } = table.state;
            resetTable(table);
            table.setRowSelection(selection);
          }}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </Flex>
  );
};
